import { useMemo } from 'react'
import type { NewDeal, Organization, Product } from 'types'
import { detectVersionableAgreements, MasterAgreement, VersionedDeal } from 'utils/agreementVersioning'

interface UseVersionDetectionArgs {
  selectedOrganization: Organization | null
  deals: NewDeal[]
  products: Product[]
}

interface UseVersionDetectionResult {
  versionableAgreements: MasterAgreement[]
  shouldShowVersionFlow: boolean
  currentVersion: VersionedDeal | null
  activeMasterAgreement: MasterAgreement | null
}

export function useVersionDetection({
  selectedOrganization,
  deals,
  products,
}: UseVersionDetectionArgs): UseVersionDetectionResult {
  return useMemo(() => {
    if (!selectedOrganization) {
      return { versionableAgreements: [], shouldShowVersionFlow: false, currentVersion: null, activeMasterAgreement: null }
    }

    const orgId = (selectedOrganization as any).org_uuid || (selectedOrganization as any).id
    const orgPK = selectedOrganization?.PK || `ORG_${orgId}`
    const orgDeals = deals?.filter(d => d?.PK === orgPK) ?? []
    const orgProducts = products?.filter(p => p?.PK === orgPK) ?? []

    const versionableAgreements = detectVersionableAgreements(orgDeals, orgProducts)
    const hasActive = versionableAgreements.length > 0

    return {
      versionableAgreements,
      shouldShowVersionFlow: hasActive,
      currentVersion: hasActive ? versionableAgreements[0].currentVersion : null,
      activeMasterAgreement: hasActive ? versionableAgreements[0] : null,
    }
  }, [selectedOrganization, deals, products])
}
